import { useMemo } from 'react';
import { useIntl } from 'react-intl';
import { pickLangText } from '../labels';
import { requirementTypeMessageId } from '../requirementTypes';
import { LangMap } from '../types';
import { buildUserViewTexts, UserViewTexts } from './texts';

/**
 * Alt søkervisningen trenger av språk, hentet én gang og memoisert.
 *
 * Modellen (`buildUserView`) er fri for `react-intl`; den tar imot ferdige
 * tekster, en oversetter for språkkart og et oppslag for kravtypene. Denne
 * hooken er broen mellom de to.
 */
export type UserViewLanguage = {
  texts: UserViewTexts;
  /** Plukker riktig språk ut av et språkkart fra kravtjenesten. */
  translate: (langMap?: LangMap | null) => string;
  /** Kravtypen på norsk/engelsk, eller tom streng for en type vi ikke kjenner. */
  requirementTypeLabel: (requirementType: string) => string;
};

export const useUserViewTexts = (): UserViewLanguage => {
  const intl = useIntl();

  return useMemo(() => {
    const requirementTypeLabel = (requirementType: string): string => {
      const id = requirementTypeMessageId(requirementType);
      return id ? intl.formatMessage({ id }) : '';
    };

    return {
      texts: buildUserViewTexts((id, values) => intl.formatMessage({ id }, values)),
      translate: (langMap?: LangMap | null) => pickLangText(langMap || undefined, intl.locale),
      requirementTypeLabel,
    };
  }, [intl]);
};
